/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type TFunction } from 'i18next';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type ActivityItem } from '../../lib/api/dashboard.js';

import { DashboardCard } from './dashboard-card.js';

const DOT_CLINICAL = 'bg-violet-500';
const DOT_MONEY_IN = 'bg-emerald-500';
const DOT_MONEY_OUT = 'bg-amber-500';
const DOT_USER = 'bg-blue-500';
const DOT_REPORT = 'bg-sky-500';

/** Maps an audit-action slug to its i18n label key and activity-feed dot colour. */
const ACTION_META: Readonly<Record<string, { readonly key: string; readonly dot: string }>> = {
  'client.created': { key: 'clientCreated', dot: DOT_CLINICAL },
  'session_note.signed': { key: 'noteSigned', dot: DOT_CLINICAL },
  'itp.version.created': { key: 'planVersion', dot: DOT_CLINICAL },
  'itp.review.recorded': { key: 'reviewRecorded', dot: DOT_CLINICAL },
  'itp.collaborator.added': { key: 'collaboratorAdded', dot: DOT_CLINICAL },
  'invoice.created': { key: 'invoiceCreated', dot: DOT_MONEY_OUT },
  'invoice.paid': { key: 'invoicePaid', dot: DOT_MONEY_IN },
  'payroll.processed': { key: 'payrollProcessed', dot: DOT_MONEY_IN },
  'user.invited': { key: 'userInvited', dot: DOT_USER },
  'user.activated': { key: 'userActivated', dot: DOT_USER },
  'report.generated': { key: 'reportGenerated', dot: DOT_REPORT },
};

/** Relative "x ago" label from an ISO instant, localised. */
function relativeTime(iso: string, t: TFunction): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) {
    return t('admin.dashboard.activity.justNow');
  }
  if (mins < 60) {
    return t('admin.dashboard.activity.minutesAgo', { count: mins });
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return t('admin.dashboard.activity.hoursAgo', { count: hours });
  }
  return t('admin.dashboard.activity.daysAgo', { count: Math.floor(hours / 24) });
}

function ActivityRow({ item }: { readonly item: ActivityItem }): JSX.Element {
  const { t } = useTranslation();
  const meta = ACTION_META[item.action];
  const actor = item.actorName ?? t('admin.dashboard.activity.system');
  const label = meta !== undefined ? t(`admin.dashboard.activity.${meta.key}`) : item.action;
  return (
    <li className="flex items-center gap-3 py-3 first:pt-0">
      <span
        className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${meta?.dot ?? 'bg-slate-400'}`}
        aria-hidden="true"
      />
      <p className="min-w-0 flex-1 text-sm text-slate-600 dark:text-slate-300">
        <span className="font-semibold text-slate-800 dark:text-slate-100">{actor}</span> {label}
      </p>
      <span className="shrink-0 text-xs text-slate-400">{relativeTime(item.occurredAt, t)}</span>
    </li>
  );
}

/** "Recent Activities" — newest clinic events from the append-only audit log. */
export function RecentActivityCard({
  items,
}: {
  readonly items: readonly ActivityItem[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <DashboardCard
      title={t('admin.dashboard.activity.title')}
      subtitle={t('admin.dashboard.activity.sub')}
    >
      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">{t('admin.dashboard.noActivity')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}
